import { friendlyEpisodeName } from "./episode-name";
/**
 * Patient data loaders — patient list, per-patient care plan with
 * enriched goals, and per-patient observation "actuals".
 *
 * Catalogue maps needed for goal enrichment are passed in via
 * PatientPlanContext so this module doesn't reach back into main.ts
 * for module globals. Planned further cleanup in Phase 5 when state
 * moves into its own module.
 *
 * Extracted from main.ts as part of OMON-031 Phase 4.
 */

import {
  collection,
  doc,
  query,
  where,
  getDoc,
  getDocs,
  DocumentReference,
  type QueryDocumentSnapshot,
  type DocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { accessPlansAll } from "../admin/claims";
import { resolveCategoryCodes } from "../catalogue/category";
import { monitoredEventNarrativeEntry } from "../clients/narrative";
import { planStewardshipOf } from "./plan-stewardship";
import { normaliseRoles } from "../clients/shift-role-policy";
import { readComponentPrompts } from "../clients/event-parts";
import { readAllowNotApplicable } from "../clients/monitored";
import { isNotApplicable } from "../clients/not-applicable";
import type {
  PatientInfo,
  CarePlanInfo,
  GoalInfo,
  MonitoredEventInfo,
  ObservationDefinition,
  GoalCategoryNode,
  GoalSubcategoryNode,
  ActualObservation,
  NarrativeEntry,
} from "../types";

export interface PatientPlanContext {
  catalogueData: Map<string, ObservationDefinition>;
  goalCategories: Map<string, GoalCategoryNode>;
  goalSubcategories: Map<string, GoalSubcategoryNode>;
  /**
   * Fetch the named observation definitions into `catalogueData` before they
   * are looked up. Lets a plan load a handful of definitions instead of the
   * whole catalogue (2026-09-17). Absent = the caller already loaded
   * everything, and lookups proceed as before.
   */
  ensureDefinitions?: (codes: string[]) => Promise<void>;
  /**
   * OMON-351: resolves once `goalCategories` / `goalSubcategories` are filled.
   * Awaited only just before the first lookup, so the hierarchy fetch overlaps
   * the episode + plan reads instead of queueing in front of them. Absent =
   * the maps are already filled.
   */
  hierarchyReady?: Promise<void>;
}

/**
 * OMON-351: read an EpisodeOfCare once and hand the SAME in-flight read to
 * every loader that opens a client (plan, client context), instead of each
 * reading it for itself.
 */
export function readEpisode(eocId: string): Promise<DocumentSnapshot<DocumentData>> {
  return getDoc(doc(db, "_episodeofcare", eocId));
}

/**
 * Load the patient list, annotated with care-plan status and draft state.
 *
 * OMON-034 Part A: widen EoC status filter to surface auto-materialised
 * pending EoCs (post-ONE-004) and on-hold plans alongside active ones.
 * OMON-035: widen CarePlan status filter to include "draft" so the patient
 * list can render a "v{N+1} · draft" pill. Tracks latest active version
 * per EoC; pre-OMON-035 documents default to v1 when `_version` is absent.
 */
/**
 * Does this token list clients with a BROAD query, or only within its own
 * care-team / managed-organisation scope?
 *
 * Admin, auditor and all-plans planners go broad (the rules bypass them).
 *
 * ☠️ Practice author goes broad too (2026-09-17). It is a non-production role
 * whose whole job is writing plans — but it carries no access map and no
 * care-team memberships, so the scoped branch returned NOTHING and the planner
 * read "No active episodes of care in the system". Found by Ed inviting
 * himself to training as practice-author through the Add user door.
 *
 * This holds where the PERMISSIVE ruleset runs — training, demo and test
 * release production's ruleset by reference. On develop, which runs
 * firestore.strict.rules, a practice author's broad query is DENIED upfront
 * (rules are not filters, and strict has no practice-author branch), so the
 * list errors there instead of reading empty. Fixing develop means changing
 * the strict ruleset — a discussion first, never a sweep.
 */
export function listsClientsBroadly(role: string, claims: Record<string, unknown>): boolean {
  return (
    role === "admin" ||
    role === "auditor" ||
    role === "practice-author" ||
    (role === "planner" && accessPlansAll(claims))
  );
}

/**
 * The observation-definition ids a plan's monitored events look up.
 *
 * ☠️ Mirrors the extraction in `loadPatientPlanData`'s monitored-events loop
 * exactly (`entry.reference.id`). If the two drift apart, the loop looks up an
 * id nobody fetched and the event renders as "(unknown monitored event …)".
 */
export function monitoredEventCodes(rawMonitored: unknown[]): string[] {
  const codes: string[] = [];
  for (const entry of rawMonitored) {
    if (!entry || typeof entry !== "object") continue;
    const ref = (entry as { reference?: { id?: string } }).reference;
    if (ref && ref.id) codes.push(ref.id);
  }
  return codes;
}

/**
 * The observation-definition ids a plan's goals resolve their titles against.
 *
 * ☠️ Mirrors the `descCode` extraction in `loadPatientPlanData`'s goal loop
 * exactly — description code first, target measure code as the fallback.
 */
export function goalDefinitionCodes(goals: Array<Record<string, unknown> | undefined>): string[] {
  const codes: string[] = [];
  for (const data of goals) {
    if (!data) continue;
    const d = data as {
      description?: { coding?: Array<{ code?: string }> };
      target?: Array<{ measure?: { coding?: Array<{ code?: string }> } }>;
    };
    const code = d.description?.coding?.[0]?.code || d.target?.[0]?.measure?.coding?.[0]?.code;
    if (code) codes.push(code);
  }
  return codes;
}

/** `listsClientsBroadly` for the signed-in user's token (false when signed out). */
export async function currentUserListsClientsBroadly(): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) return false;
  const claims = (await user.getIdTokenResult()).claims as Record<string, unknown>;
  return listsClientsBroadly(typeof claims.role === "string" ? claims.role : "", claims);
}

/** Active/draft CarePlans by doc id — what a client-list row is annotated from. */
export type PlanStatusById = Map<string, { status: string; version: number }>;

/** EpisodeOfCare statuses the client list shows. */
const LISTED_EOC_STATUSES = new Set(["active", "pending", "onhold"]);

/**
 * One client-list row from an EpisodeOfCare doc, or null when the list would
 * not show it.
 *
 * ☠️ The ONE place the list's inclusion rule and plan pills are decided. The
 * full list (`loadPatients`), the per-client refresh on every plan open
 * (`loadPatientPlan` → `row`) and the single-EoC deep link (`loadPatientRow`)
 * all go through here, so a refreshed row can never disagree with a listed
 * one. Excluded, in every path:
 *   - `template` — the agent.fhir clone source (status "active" with the
 *     _solutionInstanceName_ placeholder; rendered as a client tile on the
 *     shaped demo estate, 2026-09-08);
 *   - `active: false` — a retired EoC even when its FHIR status was left
 *     "active" (Amanda Burton's pilot15 duplicate tile, 2026-08-05);
 *   - any status other than active / pending / onhold.
 *
 * `planById` holds the episode's active + draft plans; a `carePlan[]` entry
 * absent from it (superseded, missing) is skipped.
 */
export function patientRowFromEoc(
  docId: string,
  data: Record<string, unknown> | undefined,
  planById: PlanStatusById,
): PatientInfo | null {
  if (!data || docId === "template" || data.active === false) return null;
  const status = typeof data.status === "string" ? data.status : "";
  if (!LISTED_EOC_STATUSES.has(status)) return null;

  let latestActiveVersion = 0;
  let hasDraft = false;
  const carePlanArr = (Array.isArray(data.carePlan) ? data.carePlan : []) as Array<{ reference?: { id?: string } }>;
  for (const entry of carePlanArr) {
    const ref = entry?.reference;
    if (!ref?.id) continue;
    const plan = planById.get(ref.id);
    if (!plan) continue; // not active/draft (e.g. superseded)
    if (plan.status === "active") {
      if (plan.version > latestActiveVersion) latestActiveVersion = plan.version;
    } else if (plan.status === "draft") {
      hasDraft = true;
    }
  }

  return {
    docId,
    name: friendlyEpisodeName(data, docId),
    status,
    hasPlan: latestActiveVersion > 0,
    planVersion: latestActiveVersion > 0 ? latestActiveVersion : undefined,
    hasDraft,
    teamOrgId: managingOrgId(data.managingOrganization),
  };
}

/** EoC.managingOrganization → the org's doc id. ☠️ Three shapes live: a
 *  plain {reference}, a list of them, and a bare reference (omon-api's
 *  chase._managing_org_ref tallied 280 / 10 / 1 on 2026-09-24). */
export function managingOrgId(mo: unknown): string | null {
  const entry = Array.isArray(mo) ? mo[0] : mo;
  if (!entry || typeof entry !== "object") return null;
  const ref = "reference" in entry ? (entry as { reference?: unknown }).reference : entry;
  const id = ref && typeof ref === "object" ? (ref as { id?: unknown }).id : null;
  return typeof id === "string" && id ? id : null;
}

/**
 * OMON-351: write a refreshed row back — into `patient` itself (the object the
 * detail view holds, which may be a deep-link row not in the list) and into the
 * list entry with the same id, when it is a different object. Updated IN PLACE:
 * `state.selectedPatient` and the router hold these objects by identity.
 *
 * A null row (the EoC no longer qualifies for the list) changes nothing; the
 * next full list read drops it.
 */
export function mergePatientRow(patient: PatientInfo, list: PatientInfo[], row: PatientInfo | null): void {
  if (!row || row.docId !== patient.docId) return;
  Object.assign(patient, row);
  const listed = list.find((p) => p.docId === row.docId);
  if (listed && listed !== patient) Object.assign(listed, row);
}

/**
 * OMON-351: one client, for a deep link that arrives before the list has
 * loaded. One EoC read instead of the whole list; the plan pills are filled
 * in by the plan load that follows (`loadPatientPlan` returns the refreshed
 * row). Null when the EoC is missing, unreadable, or one the list would not
 * show — the caller falls back to the list exactly as an unknown id did.
 *
 * ☠️ Applies the list's INCLUSION rule, not its SCOPE: under the permissive
 * rulesets a single get can read an EoC a scoped token's list query would
 * never return. Callers must use this only for tokens that list broadly
 * (router.ts resolveRoutePatient).
 */
export async function loadPatientRow(eocId: string): Promise<PatientInfo | null> {
  try {
    const snap = await getDoc(doc(db, "_episodeofcare", eocId));
    if (!snap.exists()) return null;
    return patientRowFromEoc(snap.id, snap.data(), new Map());
  } catch (err) {
    console.warn(`loadPatientRow: ${eocId}`, err);
    return null;
  }
}

export async function loadPatients(): Promise<PatientInfo[]> {
  // OMON-160: scope queries to the user's allowed read surface.
  // Firestore Security Rules are not filters — a broad query against a
  // collection whose rule does data-dependent checks (e.g. _episodeofcare's
  // `userOnEoc(resource.data) || userManagesEocOrg(resource.data)` planner
  // branch) is rejected upfront if any returned doc would fail the rule.
  // Admin/auditor bypass scope at the rule level, so their broad queries
  // work. Planners/carers must constrain the query to their allowed scope:
  //   - planner-as-manager → managingOrganization in managedOrganizations[]
  //   - planner/carer-on-team → careTeam[0].reference in careTeamMemberships[]
  // We fire both classes of query if the user has both kinds of scope, and
  // dedupe results.

  const user = auth.currentUser;
  if (!user) return [];
  const tokenResult = await user.getIdTokenResult();
  const claims = tokenResult.claims as Record<string, unknown>;
  const role = typeof claims.role === "string" ? claims.role : "";
  const managedOrgIds = Array.isArray(claims.managedOrganizations)
    ? (claims.managedOrganizations as string[])
    : [];
  const careTeamIds = Array.isArray(claims.careTeamMemberships)
    ? (claims.careTeamMemberships as string[])
    : [];
  // OMON-250 (gate step 3): the broad-list bypass is granted by the TOKEN,
  // not the role label — admin/auditor bypass at the rule level, and a
  // planner bypasses iff their access-map says `plans: "all"` (the strict
  // rules' plannerAllPlans() branch; under strict, a broad list from any
  // other token is DENIED upfront — rules are not filters). Today every
  // planner carries plans:"all" (OMON-234 interim projection), so behaviour
  // is unchanged; when OMON-234 lands per-org scopes, those planners fall
  // through to the scoped branch below with no further code change.
  const bypassesScope = listsClientsBroadly(role, claims);

  const eocDocs: QueryDocumentSnapshot<DocumentData>[] = [];
  const cpDocs: QueryDocumentSnapshot<DocumentData>[] = [];

  if (bypassesScope) {
    // Admin/auditor (rule bypassesCareTeamScope()) or all-plans planner
    // (rule plannerAllPlans()): broad queries are rule-provable for these
    // tokens. Preserves the pre-OMON-160 behaviour exactly.
    const [eocSnap, cpSnap] = await Promise.all([
      getDocs(query(collection(db, "_episodeofcare"), where("status", "in", ["active", "pending", "onhold"]))),
      getDocs(query(collection(db, "_carePlan"), where("status", "in", ["active", "draft"]))),
    ]);
    // Record-level `active: false` marks a retired EoC even when its FHIR
    // status was left "active" (Amanda Burton's pilot15 rendered as a
    // duplicate tile this way, 2026-08-05) — skip them in every branch.
    // `template` is the agent.fhir clone source ({collection}/template) and
    // carries status:"active" with the _solutionInstanceName_ placeholder —
    // it matched this query in EVERY environment and rendered as a client
    // tile (spotted on the shaped demo estate, 2026-09-08). Never a client.
    eocSnap.forEach((d) => { if (d.id !== "template" && d.data().active !== false) eocDocs.push(d); });
    cpSnap.forEach((d) => cpDocs.push(d));
  } else {
    // Planner/carer: pre-filter to the user's allowed scope. Can't combine
    // multiple `in` clauses in one Firestore query, so we run separate
    // queries for managed-org scope and careTeam scope, then dedupe.
    // Status filter applied client-side (only one `in` per query).
    const eocSeen = new Set<string>();

    // Manager scope: EoCs whose managingOrganization is one of the
    // user's managed orgs. Firestore `in` caps at 30 values → chunk.
    if (managedOrgIds.length > 0) {
      const CHUNK = 30;
      for (let i = 0; i < managedOrgIds.length; i += CHUNK) {
        const chunk = managedOrgIds.slice(i, i + CHUNK);
        const orgRefs = chunk.map((id) => doc(db, "_organization", id));
        const snap = await getDocs(query(
          collection(db, "_episodeofcare"),
          where("managingOrganization.reference", "in", orgRefs),
        ));
        snap.forEach((d) => {
          if (d.id === "template" || eocSeen.has(d.id)) return;
          const { status, active } = d.data();
          if (active !== false && (status === "active" || status === "pending" || status === "onhold")) {
            eocSeen.add(d.id);
            eocDocs.push(d);
          }
        });
      }
    }

    // CareTeam scope: doc-by-doc fetch instead of a query. Firestore
    // Security Rules treat list/query operations differently from single-doc
    // gets — the engine analyses whether the QUERY can be proven to only
    // return rule-passing docs, and rejects upfront if it can't (the "rules
    // are not filters" trap). The rule's `userOnEoc` does indexed access
    // (`careTeam[0].reference.id ∈ membership`) which array-contains-any
    // can't structurally match, so any planner-via-careTeam query gets
    // rejected before any per-doc eval.
    //
    // The workaround: for each `_careTeam` doc in the user's JWT
    // memberships, fetch it, read its `subject.reference` (which points
    // at the EoC the team is for), then `getDoc()` that EoC directly.
    // Single-doc reads use per-doc rule eval, so userOnEoc actually fires
    // and passes for docs whose careTeam[0] points back at this user's team.
    //
    // Requires each `_careTeam` to carry a `subject` field pointing at
    // its `_episodeofcare`. Stamped onto HCS Demo's careTeam 2026-05-26
    // as the first instance; bulk-stamp the rest as a follow-up.
    if (careTeamIds.length > 0) {
      const teamSnaps = await Promise.all(
        careTeamIds.map((id) => getDoc(doc(db, "_careTeam", id))),
      );
      const eocRefsFromTeams: DocumentReference[] = [];
      for (const teamSnap of teamSnaps) {
        if (!teamSnap.exists()) continue;
        const teamData = teamSnap.data();
        const subjectRef = teamData?.subject?.reference;
        if (subjectRef instanceof DocumentReference) {
          eocRefsFromTeams.push(subjectRef);
        }
      }
      const eocSnapsFromTeams = await Promise.all(
        eocRefsFromTeams.map((ref) => getDoc(ref)),
      );
      for (const eocSnap of eocSnapsFromTeams) {
        if (!eocSnap.exists()) continue;
        if (eocSnap.id === "template" || eocSeen.has(eocSnap.id)) continue;
        const { status, active } = eocSnap.data();
        if (active !== false && (status === "active" || status === "pending" || status === "onhold")) {
          eocSeen.add(eocSnap.id);
          // Cast: getDoc returns DocumentSnapshot but our array is typed for
          // QueryDocumentSnapshot. They share the .id / .data() / .ref surface
          // the rest of the function uses.
          eocDocs.push(eocSnap as unknown as QueryDocumentSnapshot<DocumentData>);
        }
      }
    }

    // CarePlans whose subject.reference is one of the EoCs we just read.
    // Same rule-engine constraint: CarePlan's planner branch checks
    // userOnSubjectEocData / userHasManagerAccessToSubjectEoc which both
    // get() the subject EoC and verify the user's access there. By only
    // querying CarePlans of allowed EoCs, the per-doc rule eval passes.
    if (eocDocs.length > 0) {
      const cpSeen = new Set<string>();
      const eocRefs = eocDocs.map((d) => d.ref);
      const CHUNK = 30;
      for (let i = 0; i < eocRefs.length; i += CHUNK) {
        const chunk = eocRefs.slice(i, i + CHUNK);
        const snap = await getDocs(query(
          collection(db, "_carePlan"),
          where("subject.reference", "in", chunk),
        ));
        snap.forEach((d) => {
          if (cpSeen.has(d.id)) return;
          const status = d.data().status;
          if (status === "active" || status === "draft") {
            cpSeen.add(d.id);
            cpDocs.push(d);
          }
        });
      }
    }
  }

  // Pattern A (FHIR canonical) — build a plan-by-docId lookup, then walk
  // each EoC's canonical `carePlan[]` Reference array. The plan→EoC link
  // lives ONLY on the parent (per Step B of the data-model-drift audit
  // at architecture/audits/2026-05-21-omon-data-model-drift.md). The
  // pre-Step-B4 code grouped by `data._episodeOfCareId` denorm string;
  // that field still exists in production data but readers ignore it.
  const planById: PlanStatusById = new Map();
  cpDocs.forEach((d) => {
    const data = d.data();
    planById.set(d.id, {
      status: data.status,
      version: data._version || 1,
    });
  });

  const out: PatientInfo[] = [];
  eocDocs.forEach((d) => {
    const row = patientRowFromEoc(d.id, d.data(), planById);
    if (row) out.push(row);
  });
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

/**
 * Load the active care plan for a patient + the enriched goal list, plus
 * the planner's draft (if any) so the editor can resume on
 * Create new draft. Goals are resolved against the catalogue (live titles
 * + hierarchy display) and sorted by (category order, _order, title).
 *
 * OMON-032 v1: pick only the most-recently-created active CarePlan for
 * the EoC. Legacy data may have N active plans (fixed in savePlan but
 * pre-fix records persist until each patient's next save). Without this
 * filter, every historical plan's goals were aggregated → runaway
 * duplication.
 * OMON-035: also collect the planner's draft (status:draft) for editor
 * resume.
 *
 * Does NOT load actuals — call `loadPatientActuals` separately.
 */
/** One row of a plan's version history (OMON-329). */
export interface PlanVersionInfo {
  docId: string;
  version: number | null;
  status: string;
  created: string;
  supersededAt: string | null;
  goalCount: number;
  monitoredCount: number;
  isLive: boolean;
}

/**
 * OMON-329: every version of this episode's plan, newest first.
 *
 * Reads only what a history list needs — counts and timestamps — so the view
 * costs one EoC read plus one read per version, with no goal-doc resolution.
 * `loadPatientPlan(..., { versionDocId })` does the expensive resolution, and
 * only for the one version a planner actually chooses to restore.
 *
 * ☠️ Counts come from `goal[]` and `_monitoredEvents[]`, which is right for
 * PUBLISHED versions and wrong for drafts: a draft carries neither, holding
 * its content in `_draftState` instead. Drafts are excluded here for that
 * reason — counting them reports 0/0 for a perfectly healthy draft, which is
 * exactly the false alarm that started this ticket.
 */
export async function loadPlanVersions(eocId: string): Promise<PlanVersionInfo[]> {
  const eocSnap = await getDoc(doc(db, "_episodeofcare", eocId));
  const carePlanArr = (eocSnap.data()?.carePlan || []) as Array<{ reference?: DocumentReference }>;
  const snaps = await Promise.all(
    carePlanArr
      .map((e) => e?.reference)
      .filter((r): r is DocumentReference => r instanceof DocumentReference)
      .map((r) => getDoc(r)),
  );

  const rows: PlanVersionInfo[] = [];
  for (const s of snaps) {
    if (!s.exists()) continue;
    const d = s.data()!;
    if (d.status === "draft") continue; // content lives in _draftState — see above
    const goals = Array.isArray(d.goal) ? d.goal.length : 0;
    const mon = Array.isArray(d._monitoredEvents) ? d._monitoredEvents.length : 0;
    rows.push({
      docId: s.id,
      version: typeof d._version === "number" ? d._version : null,
      status: String(d.status || ""),
      created: String(d.created || d._createdAt || ""),
      supersededAt: typeof d._supersededAt === "string" ? d._supersededAt : null,
      goalCount: goals,
      monitoredCount: mon,
      isLive: d.status === "active",
    });
  }
  // Newest first. Sort on version where present — `created` is a string
  // timestamp and two versions cut in the same second sort arbitrarily.
  rows.sort((a, b) => (b.version ?? -1) - (a.version ?? -1)
    || b.created.localeCompare(a.created));
  return rows;
}

export async function loadPatientPlan(
  patient: PatientInfo,
  ctx: PatientPlanContext,
  /**
   * OMON-329: resolve a SPECIFIC plan version instead of the current active
   * one, so restoring a superseded version reuses this entire path — goal
   * docs, catalogue late-binding, monitored-event entry fields — rather than
   * reimplementing it. The caller assigns the result into
   * `state.patientGoals` / `state.patientMonitoredEvents` and calls
   * `editPlan()`, which already knows how to turn those into builder state.
   *
   * ☠️ Superseded versions are otherwise EXCLUDED here (only
   * `status === "active"` is collected), so an old version can only be
   * reached by naming it explicitly. That is deliberate: nothing should be
   * able to surface a superseded plan by accident.
   */
  opts: {
    versionDocId?: string;
    /** OMON-351: a shared in-flight EoC read (`readEpisode`); absent = read it here. */
    episode?: Promise<DocumentSnapshot<DocumentData>>;
  } = {},
): Promise<{
  carePlans: CarePlanInfo[];
  goals: GoalInfo[];
  monitoredEvents: MonitoredEventInfo[];
  draft: CarePlanInfo | null;
  /**
   * OMON-351: this client's list row, rebuilt from the EoC + plan docs this
   * load has just read (no extra reads). The caller writes it back into the
   * list, so a publish / discard / new draft shows on the list without
   * re-reading every client. Null when the list would not show the EoC.
   */
  row: PatientInfo | null;
}> {
  const { catalogueData, goalCategories, goalSubcategories } = ctx;

  // Pattern A — traverse from EoC.carePlan[] Reference array (canonical).
  // Replaces the pre-Step-B4 `where("_episodeOfCareId", "==", ...)` denorm
  // query per architecture/audits/2026-05-21-omon-data-model-drift.md.
  const eocSnap = await (opts.episode ?? readEpisode(patient.docId));
  const eocData = eocSnap.data();
  const carePlanArr = (eocData?.carePlan || []) as Array<{ reference?: DocumentReference }>;
  const planSnapshots = await Promise.all(
    carePlanArr
      .map((entry) => entry?.reference)
      .filter((ref): ref is DocumentReference => ref instanceof DocumentReference)
      .map((ref) => getDoc(ref))
  );

  // Per-CarePlan raw _monitoredEvents[] so we can extract from the selected
  // active plan after sorting (OMON-119 / ADR-0015). Map keyed on doc ID.
  const monitoredRawByDoc = new Map<string, unknown[]>();
  const activePlans: CarePlanInfo[] = [];
  // OMON-329: the explicitly-named version, whatever its status. Captured
  // separately from activePlans so naming a version can never widen what the
  // normal (unnamed) path returns.
  let namedVersion: CarePlanInfo | null = null;
  let draft: CarePlanInfo | null = null;
  for (const d of planSnapshots) {
    if (!d.exists()) continue; // tolerate missing targets (FHIR resilient-references)
    const data = d.data()!;
    const refs = (data.goal || []).map((g: { reference: DocumentReference }) => g.reference?.id).filter(Boolean);
    // FHIR R5 stewardship, flattened for the UI. The rule (and why a flattened
    // reference must read as ABSENT rather than be parsed) lives in
    // ./plan-stewardship, which is unit-tested; the strict native-reference
    // test stays here, injected, so that module needs no Firestore import.
    const stewardship = planStewardshipOf(
      data,
      (v) => v instanceof DocumentReference,
    );
    if (Array.isArray(data._monitoredEvents)) {
      monitoredRawByDoc.set(d.id, data._monitoredEvents);
    }
    if (opts.versionDocId && d.id === opts.versionDocId) {
      namedVersion = {
        docId: d.id,
        title: data.title || d.id,
        status: data.status,
        created: data.created || "",
        goalRefs: refs,
        periodStart: data.period?.start,
        periodEnd: data.period?.end,
        _version: data._version,
        ...stewardship,
      };
    }
    if (data.status === "active") {
      activePlans.push({
        docId: d.id,
        title: data.title || d.id,
        status: data.status,
        created: data.created || "",
        goalRefs: refs,
        periodStart: data.period?.start,
        periodEnd: data.period?.end,
        _version: data._version,
        ...stewardship,
      });
    } else if (data.status === "draft") {
      draft = {
        docId: d.id,
        title: data.title || d.id,
        status: data.status,
        created: data.created || "",
        goalRefs: refs,
        _draftState: data._draftState,
        _draftLastSavedAt: data._draftLastSavedAt,
      };
    }
  }
  activePlans.sort((a, b) => b.created.localeCompare(a.created));

  // OMON-351: the list row, from the same snapshots (see the return type).
  const planStatusById: PlanStatusById = new Map();
  for (const d of planSnapshots) {
    if (!d.exists()) continue;
    const pd = d.data()!;
    if (pd.status === "active" || pd.status === "draft") {
      planStatusById.set(d.id, { status: pd.status, version: pd._version || 1 });
    }
  }
  const row = eocSnap.exists() ? patientRowFromEoc(eocSnap.id, eocData, planStatusById) : null;

  const carePlans: CarePlanInfo[] = [];
  const goalDocIds: string[] = [];
  const monitoredEvents: MonitoredEventInfo[] = [];
  // OMON-329: a named version wins over the newest active one. When the
  // caller named a version that does not exist, fall through to the normal
  // path rather than returning an empty plan — a bad id must not look like
  // "this client has no plan".
  const selected = namedVersion ?? (activePlans.length > 0 ? activePlans[0] : null);

  // OMON-351: start the goal reads now — they need only the selected plan's
  // goal ids, not the monitored-event definitions below — so the two waits
  // overlap instead of running back to back. OMON-088 (per-doc gets, never an
  // `in` query) still applies; see the goal block further down.
  const goalSnapsPromise = Promise.all(
    (selected ? selected.goalRefs : []).map((id) => getDoc(doc(db, "_goal", id))),
  );
  // Never an unhandled rejection if the monitored block throws first; the
  // await below still sees the failure.
  goalSnapsPromise.catch(() => {});

  // Lookups in both blocks below read the goal hierarchy maps.
  await ctx.hierarchyReady;

  if (selected) {
    const current = selected;
    carePlans.push(current);
    goalDocIds.push(...current.goalRefs);
    // Extract monitored events from the selected active plan.
    // Each entry has shape {reference: DocRef(_observationDefinition/{code}), dueDate, targetMeasure, ...}.
    const rawMonitored = monitoredRawByDoc.get(current.docId) || [];
    // Fetch exactly the definitions this loop is about to look up.
    await ctx.ensureDefinitions?.(monitoredEventCodes(rawMonitored));
    for (const entry of rawMonitored) {
      if (!entry || typeof entry !== "object") continue;
      const m = entry as Record<string, unknown>;
      const ref = m.reference as DocumentReference | undefined;
      const odCode = ref && ref.id ? ref.id : "";
      if (!odCode) continue;
      const od = catalogueData.get(odCode);
      // Live-resolve subcategory display via goalSubcategories map. Resolve the
      // subcategory code by SYSTEM URI, not array index — the category[] order
      // is not stable (capability codings live at arbitrary indices, and the
      // observation-category marker strip changed lengths). ADR-0022.
      const subcatCode = resolveCategoryCodes(od?.category).goalSubcategory;
      // Subcategories are keyed by `${categoryCode}__${subcategoryCode}` in the map.
      // We don't know the parent category here, so walk the values.
      let subcategoryDisplay: string | undefined;
      if (subcatCode) {
        for (const node of goalSubcategories.values()) {
          if (node.code === subcatCode) {
            subcategoryDisplay = node.display;
            break;
          }
        }
      }
      // Suppress unused-warning on goalCategories (not needed for this shape — kept for future fields).
      void goalCategories;
      monitoredEvents.push({
        odCode,
        title: od?.title || `(unknown monitored event: ${odCode})`,
        subcategory: subcategoryDisplay,
        recordingKind: (od as { _recordingKind?: string } | undefined)?._recordingKind,
        targetMeasure: typeof m.targetMeasure === "number" ? m.targetMeasure : undefined,
        dueDate: typeof m.dueDate === "string" ? m.dueDate : undefined,
        migratedFromGoal: typeof m._migratedFromGoal === "string" ? m._migratedFromGoal : undefined,
        lifecycleStatus: typeof m.lifecycleStatus === "string" ? m.lifecycleStatus : undefined,
        notes: m.notes,
        // OMON-174: the entry's snapshot prompt; legacy entries predate the
        // field, so fall back to the OD's live canonical.
        prompt: typeof m._prompt === "string" && m._prompt
          ? m._prompt
          : (od?._monitoredEventPrompt || undefined),
        // OMON-232: the entry's per-part prompts (absent → the fact's defaults).
        componentPrompts: readComponentPrompts(m._componentPrompts),
        // OMON-179/177: the entry's frozen per-shift policy values. Absent =
        // affordance not granted at add time; explicit false = deactivated.
        mandatory: typeof m._mandatory === "boolean" ? m._mandatory : undefined,
        shiftRoles: normaliseRoles(m._shiftRoles),
        // ADR-0035 am.9a: carers may answer "Not applicable on this shift".
        allowNotApplicable: readAllowNotApplicable(m._allowNotApplicable),
        escalate: typeof m._escalate === "boolean" ? m._escalate : undefined,
        escalationThreshold: typeof m._escalationThreshold === "number" ? m._escalationThreshold : undefined,
        escalationDirection: m._escalationDirection === "below" ? "below" : "above",
        targetValue: typeof m._targetValue === "number" ? m._targetValue : undefined,
      });
    }
  }

  const goals: GoalInfo[] = [];
  if (goalDocIds.length > 0) {
    // OMON-088: per-doc fetch instead of a single bulk query. The rule
    // for `_goal` is data-dependent (`userOnSubjectEocData` does
    // get(subject.reference) → userOnEoc), so a `where(documentId(), "in", …)`
    // query gets rejected by the rules engine for non-admin users (the
    // engine can't structurally prove every id in the `in` list will
    // satisfy the rule's get()-chain check). Single-doc reads use per-doc
    // rule eval which works correctly. Read count is identical; we just
    // pay N round-trips instead of N/30. Acceptable at plan scale (~10 goals).
    const goalSnaps = await goalSnapsPromise;
    // Fetch exactly the definitions the goal titles below resolve against.
    await ctx.ensureDefinitions?.(
      goalDefinitionCodes(goalSnaps.filter((d) => d.exists()).map((d) => d.data())),
    );
    goalSnaps.forEach((d) => {
      if (d.exists()) {
        // Filter out non-existent goals (referenced from EoC.carePlan
        // but no corresponding doc — FHIR resilient-reference semantics).
        const data = d.data();
        // OMON-004 Decision 1: resolve title live from catalogue via stable code
        const descCode = data.description?.coding?.[0]?.code || data.target?.[0]?.measure?.coding?.[0]?.code;
        const liveTitle = (descCode && catalogueData.get(descCode)?.title) || data.description?.text || d.id;
        // OMON-079 79c (2026-05-10): late-bind per ADR-0007. Codes are
        // canonical; display strings come from the hierarchy maps loaded
        // in main.ts. Snapshot fallbacks dropped — orphan codes render
        // as `(unknown ...)` so drift surfaces explicitly rather than
        // silently using a stale string.
        const rawCatCode = data.category?.[0]?.coding?.[0]?.code;
        const rawAimCode = data.category?.[1]?.coding?.[0]?.code;
        const categoryCode = (rawCatCode && goalCategories.has(rawCatCode)) ? rawCatCode : undefined;
        const subcategoryCode = (rawAimCode && categoryCode && goalSubcategories.has(`${categoryCode}__${rawAimCode}`)) ? rawAimCode : undefined;
        goals.push({
          docId: d.id,
          description: liveTitle,
          lifecycleStatus: data.lifecycleStatus || "proposed",
          achievementStatus: data.achievementStatus?.coding?.[0]?.code || "in-progress",
          categoryCode,
          storedCategoryCode: typeof rawCatCode === "string" ? rawCatCode : undefined,
          category: categoryCode
            ? goalCategories.get(categoryCode)?.display
            : (rawCatCode ? `(unknown Goal Category: ${rawCatCode})` : "(unknown Goal Category)"),
          subcategoryCode,
          // ☠️ UNRESOLVED ⇒ UNDEFINED, not a placeholder string (Ed,
          // 2026-09-02). Every consumer builds its breadcrumb with
          // `[category, subcategory].filter(Boolean)`, so a placeholder is
          // truthy and rendered — putting "(unknown Goal Sub-category)" on a
          // client's goal row, where it reads as a data fault the reader can
          // do nothing about and pushes the actual goal text down the card.
          //
          // A missing sub-category is not an error here: most goals have no
          // meaningful one. Only the CATALOGUE needs to be told (a librarian
          // can re-file it), and `resolveAimDisplay` still says so there.
          subcategory: (categoryCode && subcategoryCode)
            ? goalSubcategories.get(`${categoryCode}__${subcategoryCode}`)?.display
            : undefined,
          targetScore: data.target?.[0]?.detailInteger,
          currentScore: data._currentScore,
          measureCode: data.target?.[0]?.measure?.coding?.[0]?.code,
          _baselineMonths: data._baselineMonths,
          _baselineFixed: data._baselineFixed,
          _trajectory: data._trajectory,
          _smartGoal: data._smartGoal,
          mandatory: data._mandatory === true,
          // ADR-0035 am.8: the goal's role set (absent on goals written before).
          shiftRoles: normaliseRoles(data._shiftRoles),
          // OMON-135: surface the per-plan carer-guidance override so
          // viewers + editPlan can prefer it over canonical. Absent ⇒
          // editPlan re-snapshots canonical at clone-into-builder time.
          _carerGuidance: Array.isArray(data._carerGuidance) && data._carerGuidance.length === 5
            ? data._carerGuidance
            : undefined,
          _carerGuidanceText: typeof data._carerGuidanceText === "string" ? data._carerGuidanceText : undefined,
          _order: typeof data._order === "number" ? data._order : undefined,
        });
      }
    });
  }

  // OMON-108: sort by `_order` only — now a flat global index per goal,
  // set at plan save time from the planner's drag-reordered sequence.
  // The previous (categoryOrder, _order, title) 3-stage sort imposed
  // category grouping at read time, which conflicted with the goal of
  // letting planners shape the carer's day across categories. Legacy
  // plans with within-category `_order` values render in a stable but
  // category-grouped-looking order during the transition — additive
  // change, no migration needed.
  goals.sort((a, b) => {
    const oa = a._order ?? 999;
    const ob = b._order ?? 999;
    if (oa !== ob) return oa - ob;
    return (a.description || "").localeCompare(b.description || "");
  });

  return { carePlans, goals, monitoredEvents, draft, row };
}

/**
 * Load observation "actuals" for a patient — measurements entered by
 * carers that the chart plots against the goal trajectory.
 *
 * Returns an empty record on error. Caller may inspect console for
 * details if something went wrong.
 */
export async function loadPatientActuals(
  patient: PatientInfo,
  planStartDate: string,
): Promise<{ actuals: Record<string, ActualObservation[]>; narrative: NarrativeEntry[] }> {
  const out: Record<string, ActualObservation[]> = {};
  // OMON-129: the Combined-narrative tab is a SECOND projection over the same
  // snapshot — both observation categories, with or without notes.
  const narrative: NarrativeEntry[] = [];
  try {
    const eocRef = doc(db, "_episodeofcare", patient.docId);

    // Pattern A — query on `subject.reference` only; filter to the
    // `outcome-assessment` category client-side by reading nested
    // `category[0].coding[0].code`. Step C3 of the data-model-drift audit
    // (architecture/audits/2026-05-21-omon-data-model-drift.md) retired the
    // `_categoryCode` denorm query. Firestore can't index inside
    // array-of-maps fields, so this filter MUST run client-side; the
    // subject.reference filter keeps the result set small.
    //
    // ☠️ Deliberately NOT bounded by plan start or paginated (OMON-351
    // finding 3, declined 2026-09-24). Only the Combined narrative filters to
    // the plan period (buildNarrativeDays(…, periodStart)); every other
    // consumer of this snapshot reads the episode's WHOLE history:
    //   - trajectory charts widen their pan bounds to the earliest actual
    //     (trajectoryBounds), so pre-plan scores are reachable by panning;
    //   - monitored-event charts start their window at the OLDEST recording
    //     (windowForRecordings) and bucket every day from there;
    //   - the diary timeline and the printed shift log are "the whole record"
    //     (diaryTimelineAll) — no window at all.
    // A plan-start bound would silently drop points those screens show today,
    // and a paged narrative would leave the charts short. Scale does not force
    // it either: ~8.8k observations across ~700 episodes in prod (2026-09-24).
    // What this ticket did instead is start this read in parallel with the
    // plan and context reads (main.ts loadPatientPlan).
    const obsSnap = await getDocs(
      query(
        collection(db, "_observation"),
        where("subject.reference", "==", eocRef),
      )
    );

    // Plan start from planStartDate (for computing monthIndex)
    const psd = new Date(planStartDate);
    const planStart = new Date(psd.getFullYear(), psd.getMonth(), 1);

    obsSnap.forEach((d) => {
      const data = d.data();
      // Client-side category filter (Pattern A) replaces the retired
      // `_categoryCode` query filter.
      const categoryCode = data.category?.[0]?.coding?.[0]?.code;
      const code = data.code?.coding?.[0]?.code;
      if (!code) return;

      // OMON-129: monitored-event recordings join the narrative (ordinal
      // states and OMON-180 tally occurrences), then we're done with them —
      // the chart path below stays outcome-assessment-only.
      if (categoryCode === "monitored-event") {
        // Surface EVERY monitored-event recording in the narrative, across all
        // regimes plus note-only entries (ADR-0022 carer notes). The regime
        // branching lives in monitoredEventNarrativeEntry (unit-tested).
        const entry = monitoredEventNarrativeEntry(data, code);
        if (entry) narrative.push(entry);
        return;
      }
      if (categoryCode !== "outcome-assessment") return;
      // ADR-0035 am.9b: a "Not applicable on this shift" answer on a goal is
      // narrated, never charted (it has no score, so it would be dropped
      // below anyway — handled here so it is not dropped silently).
      if (isNotApplicable(data)) {
        const entry = monitoredEventNarrativeEntry(data, code);
        if (entry) narrative.push(entry);
        return;
      }
      const score = data.valueInteger ?? data.valueQuantity?.value;
      if (score == null) return;

      const dateStr = data.effectiveDateTime ?? "";
      const obsDate = new Date(dateStr);
      const monthIndex = (obsDate.getFullYear() - planStart.getFullYear()) * 12
        + (obsDate.getMonth() - planStart.getMonth());

      // Extract performer and note metadata for chart tooltips.
      // Pattern A (FHIR canonical) — performer identity reads from
      // `performer[0].reference.id` + `.display` per Step C1 of the
      // data-model-drift audit
      // (architecture/audits/2026-05-21-omon-data-model-drift.md).
      let performerName: string | undefined;
      let performerId: string | undefined;
      if (Array.isArray(data.performer) && data.performer.length > 0) {
        performerName = data.performer[0].display;
        const ref = data.performer[0].reference;
        if (ref && typeof ref === "object" && "id" in ref) {
          performerId = (ref as DocumentReference).id;
        }
      }

      let note: string | undefined;
      if (Array.isArray(data.note) && data.note.length > 0) {
        note = data.note[0].text;
      }

      if (!out[code]) out[code] = [];
      out[code].push({
        score,
        date: dateStr,
        performerName,
        performerId,
        note,
        monthIndex,
      });
      // OMON-129: same recording, narrative projection.
      narrative.push({ date: dateStr, code, kind: "score", score, performerName, note });
    });

    // Sort each measure's observations by date
    for (const code of Object.keys(out)) {
      out[code].sort((a, b) => a.date.localeCompare(b.date));
    }
    narrative.sort((a, b) => a.date.localeCompare(b.date));
  } catch (err) {
    // Observations may legitimately not exist yet for a new patient, but
    // permission-denied / missing-index errors should not be silent.
    // Log so they surface in the console; still return {} so callers can
    // render an empty chart without error state.
    console.warn(`loadPatientActuals: ${patient.docId}`, err);
  }
  return { actuals: out, narrative };
}
